import type { SortItem, SortEvent, SortAlgorithm } from '@/core/types';
import { EventRecorderImpl } from '@/core/EventRecorderImpl';

export class QuickSort implements SortAlgorithm {
  readonly name = 'Quick Sort';
  readonly category = 'comparison' as const;
  readonly stable = false;
  readonly inPlace = true;
  readonly adaptive = false;
  readonly best = 'O(n log n) - balanced partitions';
  readonly average = 'O(n log n) - typical randomized partition';
  readonly worst = 'O(n^2) - highly unbalanced partitions';
  readonly space = 'O(log n) - recursion stack depth';

  run(input: SortItem[]): SortEvent[] {
    const recorder = new EventRecorderImpl();
    const arr = input.map(item => ({ ...item }));
    this.quickSort(arr, 0, arr.length - 1, recorder);
    recorder.record({ type: 'done', indices: arr.map((_, i) => i) });
    return recorder.events;
  }

  private quickSort(arr: SortItem[], low: number, high: number, recorder: EventRecorderImpl): void {
    if (low >= high) return;
    const pivotIndex = this.partition(arr, low, high, recorder);
    recorder.record({ type: 'pivot', index: pivotIndex });
    this.quickSort(arr, low, pivotIndex - 1, recorder);
    this.quickSort(arr, pivotIndex + 1, high, recorder);
  }

  private partition(arr: SortItem[], low: number, high: number, recorder: EventRecorderImpl): number {
    const pivot = arr[high];
    let i = low - 1;

    for (let j = low; j < high; j++) {
      recorder.record({ type: 'compare', indices: [j, high] });
      if (arr[j].value < pivot.value) {
        i++;
        recorder.record({ type: 'swap', indices: [i, j] });
        const tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
        arr[i].currentIndex = i; arr[j].currentIndex = j;
      }
    }

    recorder.record({ type: 'swap', indices: [i + 1, high] });
    const tmp = arr[i + 1]; arr[i + 1] = arr[high]; arr[high] = tmp;
    arr[i + 1].currentIndex = i + 1; arr[high].currentIndex = high;
    return i + 1;
  }
}
